import {
  DataType,
  Form,
  FormOptions,
  Input,
  Section,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SettlementTableModel } from '../../../../api/Argentia/endpoints/settlements/model/settlements'
import { EnumDominioTarea } from '../../../../api/oData/enums/enumDominioTarea'
import { EnumEstadoTarea } from '../../../../api/oData/enums/enumEstadoTarea'
import { CardActionProps } from '../../../../components/shared/cardActions/cardActions'
import SimpleDialog from '../../../../components/shared/simpleDialog/simpleDialog'
import { getCustomerDomain } from '../../../../redux'
import { bulkUpdateObjections, getObjectionMasters } from '../../../../services/objections.services'

interface TerminateObjectionsDialogFormData {
  codes?: string[]
  finalizeAll?: boolean
}

interface TerminateObjectionsDialogProps {
  open: boolean
  handleClose: () => void
  /** Rows selected in the SettlementsTableCard */
  selectedRows: SettlementTableModel[]
  /** Domain used to fetch the master objection list and sent in relatedEntity */
  domain: EnumDominioTarea
  /** Field of SettlementTableModel to use as entity id in relatedEntity.ids */
  entityIdField: 'id' | 'invoiceId'
  /** Pre-selected objection master id — resolved to code/value after descriptions are loaded */
  /** Pre-selected objection master id — resolved to code/value after descriptions are loaded */
  /**
   * Pre-selected objection master id — resolved to code/value after descriptions are loaded
   */
  defaultObjMasterId?: number
  /** Called after a successful submit — use to refresh parent data */
  onSuccess?: () => void
}

const emptyFormData: TerminateObjectionsDialogFormData = {
  codes: undefined
}

export const TerminateObjectionsDialog: React.FC<TerminateObjectionsDialogProps> = ({
  open,
  handleClose,
  selectedRows,
  domain,
  entityIdField,
  defaultObjMasterId,
  onSuccess
}) => {
  const [t] = useTranslation(['settlements'])
  const { info, error } = useNotify()

  const [formData, setFormData] = useState<TerminateObjectionsDialogFormData>(emptyFormData)
  const [formValid, setFormValid] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isFetchingDescriptions, setIsFetchingDescriptions] = useState(false)
  const [codeOptions, setCodeOptions] = useState<SelectOption[]>([])

  // Reset and load data when dialog opens
  useEffect(() => {
    if (open) {
      setFormData(emptyFormData)
      setFormValid(false)
      loadDescriptions()
    }
  }, [open])

  const loadDescriptions = async () => {
    setIsFetchingDescriptions(true)
    await getObjectionMasters(domain)
      .then(res => {
        if (res.success && res.data) {
          setCodeOptions(
            res.data.map(item => ({
              value: item.codObjecion,
              label: `${item.codObjecion} - ${item.descripcion}`
            }))
          )
          if (defaultObjMasterId) {
            const match = res.data.find(item => item.id === defaultObjMasterId)
            if (match) {
              setFormData(prev => ({ ...prev, codes: [match.codObjecion] }))
              setFormValid(true)
            }
          }
        }
      })
      .finally(() => setIsFetchingDescriptions(false))
  }

  useEffect(() => {
    const hasCodes = (formData.codes?.length ?? 0) > 0
    setFormValid(formData.finalizeAll === true || hasCodes)
  }, [formData])

  const handleSubmit = useCallback(async () => {
    if (!formValid) return

    const ids = selectedRows
      .map(r => r[entityIdField])
      .filter((id): id is number => id !== undefined)

    const payload = {
      relatedEntity: [{ ids, codes: formData.finalizeAll ? [] : formData.codes!, domain }],
      state: EnumEstadoTarea.Finalizada
    }

    setIsSubmitting(true)
    const response = await bulkUpdateObjections(payload)
    setIsSubmitting(false)

    if (response.success) {
      info(t('settlements:dialogs.terminateObjections.success'))
      handleClose()
      onSuccess?.()
    } else {
      error(t('settlements:dialogs.terminateObjections.error'))
    }
  }, [formData, formValid, selectedRows, entityIdField, domain, handleClose])

  const sections: Section[] = useMemo(
    () => [
      {
        inputs: [
          {
            name: 'codes',
            label: t('settlements:dialogs.terminateObjections.codesLabel'),
            type: DataType.MULTISELECT,
            inputSize: 12,
            required: !formData.finalizeAll,
            readOnly: formData.finalizeAll === true,
            selectOptions: codeOptions,
            isLoading: isFetchingDescriptions,
            onChange: (value: unknown) => {
              setFormData(prev => ({ ...prev, codes: value as string[] }))
            }
          },
          {
            name: 'finalizeAll',
            label: t('settlements:dialogs.terminateObjections.terminateAllLabel'),
            type: DataType.SWITCH,
            inputSize: 12,
            onChange: (value: unknown) => {
              setFormData(prev => ({
                ...prev,
                finalizeAll: value as unknown as boolean,
                codes: value ? [] : prev.codes
              }))
            }
          }
        ] as Input[]
      }
    ],
    [t, codeOptions, isFetchingDescriptions, formData.finalizeAll]
  )

  const formOptions: FormOptions = useMemo(
    () => ({
      elevation: 0,
      columns: 2,
      analytics: {
        customer: getCustomerDomain(),
        view: 'settlements:terminateObjectionsDialog'
      }
    }),
    []
  )

  const footerActions: CardActionProps[] = useMemo(
    () => [
      {
        label: t('settlements:dialogs.terminateObjections.cancelButton'),
        color: 'secondary',
        handleActionClick: handleClose
      },
      {
        label: t('settlements:dialogs.terminateObjections.confirmButton'),
        color: 'primary',
        disabled: !formValid || isSubmitting,
        handleActionClick: handleSubmit
      }
    ],
    [t, formValid, isSubmitting, handleClose, handleSubmit]
  )

  return (
    <SimpleDialog
      open={open}
      handleClose={handleClose}
      // title={t('settlements:dialogs.terminateObjections.title')}
      footerActions={footerActions}
      maxWidth="sm"
      formStyle="formContent"
    >
      <Form
        sections={sections}
        data={formData}
        options={formOptions}
        title={t('settlements:dialogs.terminateObjections.formTitle')}
      />
    </SimpleDialog>
  )
}
